import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Package, Edit, Calculator } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", default_price: "", unit: "each", taxable: true, addons: [], active: true, reengagement_days: "", reengagement_template: "" };
const EMPTY_EST = {
  calculator_key: "",
  pricing_method: "unit",
  unit_price: 0,
  base_charge: 0,
  min_charge: 0,
  material_cost_per_unit: 0,
  material_markup_pct: 0,
  labor_rate_per_hour: 0,
  production_rate: "",
  waste_pct: 0,
  notes: "",
};

export default function Services() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(null); // null = "Uncategorized" | "all" | cat_id
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [calculators, setCalculators] = useState([]);
  const [estCfg, setEstCfg] = useState(EMPTY_EST);
  const [showEst, setShowEst] = useState(false);

  const load = async () => {
    const [{ data: svcs }, { data: cats }] = await Promise.all([
      api.get("/services"),
      api.get("/service-categories"),
    ]);
    setItems(svcs);
    setCategories(cats);
    if (activeCategoryId === null && cats.length > 0) setActiveCategoryId("all");
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    api.get("/estimator/calculators").then((r) => setCalculators(r.data.calculators || [])).catch(() => {});
  }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setEstCfg(EMPTY_EST); setShowEst(false); setOpen(true); };
  const openEdit = (s) => {
    setForm({ ...EMPTY, ...s });
    setEditId(s.id);
    setEstCfg({ ...EMPTY_EST, ...(s.estimator_config || {}) });
    setShowEst(!!(s.estimator_config && s.estimator_config.calculator_key));
    setOpen(true);
  };

  const addAddon = () => setForm((f) => ({ ...f, addons: [...(f.addons||[]), { name: "", price: 0, taxable: true }] }));
  const setAddon = (i, key, val) => setForm((f) => ({ ...f, addons: f.addons.map((a, idx) => idx === i ? { ...a, [key]: val } : a) }));
  const rmAddon = (i) => setForm((f) => ({ ...f, addons: f.addons.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    // Blank/unconfigured price → null (rendered as "—"). Preserves the
    // "not configured" distinction from a legitimate $0.
    const priceRaw = form.default_price;
    const priceVal =
      priceRaw === "" || priceRaw === null || priceRaw === undefined
        ? null
        : Number(priceRaw);
    const payload = {
      ...form,
      default_price: Number.isFinite(priceVal) ? priceVal : null,
      addons: form.addons.map((a) => ({ ...a, price: Number(a.price) || 0 })),
    };
    // Coerce optional reengagement_days ("" → null)
    if (payload.reengagement_days === "" || payload.reengagement_days === null || payload.reengagement_days === undefined) {
      payload.reengagement_days = null;
    } else {
      payload.reengagement_days = parseInt(payload.reengagement_days) || null;
    }
    if (!payload.reengagement_template) payload.reengagement_template = null;
    try {
      let savedId = editId;
      if (editId) await api.put(`/services/${editId}`, payload);
      else {
        const { data } = await api.post("/services", payload);
        savedId = data.id;
      }
      // Save estimator config only if user has expanded / filled it
      if (showEst && savedId) {
        const cfg = {
          calculator_key: estCfg.calculator_key || null,
          pricing_method: estCfg.pricing_method || "unit",
          unit_price: Number(estCfg.unit_price) || 0,
          base_charge: Number(estCfg.base_charge) || 0,
          min_charge: Number(estCfg.min_charge) || 0,
          material_cost_per_unit: Number(estCfg.material_cost_per_unit) || 0,
          material_markup_pct: Number(estCfg.material_markup_pct) || 0,
          labor_rate_per_hour: Number(estCfg.labor_rate_per_hour) || 0,
          production_rate: estCfg.production_rate === "" ? null : Number(estCfg.production_rate),
          waste_pct: Number(estCfg.waste_pct) || 0,
          notes: estCfg.notes || "",
        };
        await api.put(`/estimator/services/${savedId}/config`, cfg);
      }
      toast.success(editId ? "Service updated" : "Service created");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete service "${s.name}"?`)) return;
    try { await api.delete(`/services/${s.id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Catalog</div>
          <h1 className="font-heading text-4xl font-bold">Services</h1>
          <p className="text-sm text-zinc-500 mt-1">Your reusable service catalog with default pricing and add-ons.</p>
        </div>
        <Button onClick={openNew} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="services-new-btn">
          <Plus className="w-4 h-4 mr-1"/> New service
        </Button>
      </div>

      {/* Category tiles — top-level browse */}
      {categories.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="service-category-tiles">
          <button
            type="button"
            onClick={() => setActiveCategoryId("all")}
            className={`text-left rounded-xl p-4 border transition ${activeCategoryId === "all" ? "border-[#FF5A00] bg-orange-50" : "border-zinc-200 bg-white hover:border-zinc-400"}`}
            data-testid="service-category-tile-all"
          >
            <div className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500">All</div>
            <div className="font-heading font-bold text-lg">Every service</div>
            <div className="text-xs text-zinc-500 mt-1">{items.length} total</div>
          </button>
          {categories.map((c) => {
            const count = items.filter((s) => s.service_category_id === c.id).length;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveCategoryId(c.id)}
                className={`text-left rounded-xl p-4 border transition ${activeCategoryId === c.id ? "border-[#FF5A00] bg-orange-50" : "border-zinc-200 bg-white hover:border-zinc-400"}`}
                data-testid={`service-category-tile-${c.id}`}
              >
                <div className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500">Category</div>
                <div className="font-heading font-bold text-lg truncate">{c.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{count} {count === 1 ? "service" : "services"}</div>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setActiveCategoryId("uncategorized")}
            className={`text-left rounded-xl p-4 border transition ${activeCategoryId === "uncategorized" ? "border-[#FF5A00] bg-orange-50" : "border-zinc-200 bg-white hover:border-zinc-400"}`}
            data-testid="service-category-tile-uncategorized"
          >
            <div className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500">Fallback</div>
            <div className="font-heading font-bold text-lg">Uncategorized</div>
            <div className="text-xs text-zinc-500 mt-1">{items.filter((s) => !s.service_category_id).length} services</div>
          </button>
        </div>
      )}
      {categories.length === 0 && (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-6 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="services-empty-state">
          <div>
            <div className="font-heading font-bold text-lg">Group your services with categories</div>
            <div className="text-sm text-zinc-600">Install an industry template from Settings → Industries, or add a category manually.</div>
          </div>
          <a href="/app/settings" className="text-[#FF5A00] hover:underline text-sm font-medium">Open Settings →</a>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items
          .filter((s) => {
            if (activeCategoryId === "all") return true;
            if (activeCategoryId === "uncategorized") return !s.service_category_id;
            if (!activeCategoryId) return true;
            return s.service_category_id === activeCategoryId;
          })
          .map((s) => (
          <div key={s.id} className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid={`service-card-${s.id}`}>
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                  <Package className="w-5 h-5 text-[#FF5A00]"/>
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-lg truncate">{s.name}</div>
                  <div className="text-xs text-zinc-500">{s.taxable ? "Taxable" : "Non-taxable"} · per {s.unit}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={()=>openEdit(s)} className="text-zinc-400 hover:text-zinc-900 p-1" data-testid={`service-edit-${s.id}`}><Edit className="w-4 h-4"/></button>
                <button onClick={()=>remove(s)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`service-delete-${s.id}`}><Trash2 className="w-4 h-4"/></button>
              </div>
            </div>
            {s.description && <div className="text-sm text-zinc-600 mb-3 line-clamp-2">{s.description}</div>}
            <div
              className="font-mono-accent text-2xl font-bold text-zinc-900"
              data-testid={`service-card-price-${s.id}`}
            >
              {s.default_price === null || s.default_price === undefined || s.default_price === ""
                ? "—"
                : `$${Number(s.default_price).toLocaleString()}`}
            </div>
            {s.addons && s.addons.length > 0 && (
              <div className="mt-3 pt-3 border-t border-zinc-100">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent mb-2">Add-ons</div>
                <div className="space-y-1">
                  {s.addons.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-zinc-700">{a.name}</span>
                      <span className="font-mono-accent text-zinc-900">+${Number(a.price||0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center py-16 bg-white border border-dashed border-zinc-300 rounded-xl">
            <Package className="w-8 h-8 text-zinc-300 mx-auto mb-2"/>
            <div className="text-zinc-500">No services yet.</div>
            <Button onClick={openNew} className="mt-4 bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift">Create your first service</Button>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-form-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Edit service" : "New service"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="service-form-name"/>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={form.description||""} onChange={(e)=>setForm({...form, description: e.target.value})} data-testid="service-form-description"/>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Default price ($)</Label>
                <Input type="number" step="0.01" value={form.default_price ?? ""} onChange={(e)=>setForm({...form, default_price: e.target.value})} placeholder="Leave blank for —" data-testid="service-form-price"/>
              </div>
              <div>
                <Label>Unit</Label>
                <Input value={form.unit||""} onChange={(e)=>setForm({...form, unit: e.target.value})} placeholder="each, hour, sqft" data-testid="service-form-unit"/>
              </div>
              <div>
                <Label>Taxable</Label>
                <div className="flex items-center h-10 gap-2">
                  <input type="checkbox" checked={!!form.taxable} onChange={(e)=>setForm({...form, taxable: e.target.checked})} data-testid="service-form-taxable"/>
                  <span className="text-sm text-zinc-600">Charge tax</span>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Add-ons (optional)</Label>
                <button type="button" onClick={addAddon} className="text-xs text-[#FF5A00] hover:underline" data-testid="service-form-add-addon">+ Add</button>
              </div>
              <div className="space-y-2">
                {(form.addons||[]).map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input placeholder="Name" value={a.name} onChange={(e)=>setAddon(i, "name", e.target.value)} className="flex-1"/>
                    <Input type="number" step="0.01" placeholder="Price" value={a.price} onChange={(e)=>setAddon(i, "price", e.target.value)} className="w-28"/>
                    <button type="button" onClick={()=>rmAddon(i)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4"/></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="pt-3 mt-1 border-t border-zinc-100">
              <button
                type="button"
                onClick={() => setShowEst((v) => !v)}
                className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-600 hover:text-[#FF5A00] font-mono-accent"
                data-testid="service-form-toggle-estimator"
              >
                <Calculator className="w-3.5 h-3.5"/> Smart Estimator config {showEst ? "▼" : "▶"}
              </button>
              {showEst && (
                <div className="mt-3 space-y-3">
                  <p className="text-xs text-zinc-500">
                    Optional: enable this service in the Smart Estimator with a calculator and pricing method. Leave blank to just use the default price above.
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Calculator</Label>
                      <select
                        className="h-10 w-full rounded-md border border-zinc-300 px-3 text-sm bg-white"
                        value={estCfg.calculator_key || ""}
                        onChange={(e) => setEstCfg({ ...estCfg, calculator_key: e.target.value })}
                        data-testid="service-form-est-calc"
                      >
                        <option value="">— none —</option>
                        {calculators.map((c) => (
                          <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Pricing method</Label>
                      <select
                        className="h-10 w-full rounded-md border border-zinc-300 px-3 text-sm bg-white"
                        value={estCfg.pricing_method}
                        onChange={(e) => setEstCfg({ ...estCfg, pricing_method: e.target.value })}
                        data-testid="service-form-est-method"
                      >
                        {["unit", "area", "linear", "volume", "weight", "base_plus_area", "labor_material", "flat"].map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>$ / unit</Label>
                      <Input type="number" step="0.01" value={estCfg.unit_price} onChange={(e) => setEstCfg({ ...estCfg, unit_price: e.target.value })} data-testid="service-form-est-unitprice"/>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Min charge $</Label>
                      <Input type="number" step="0.01" value={estCfg.min_charge} onChange={(e) => setEstCfg({ ...estCfg, min_charge: e.target.value })} data-testid="service-form-est-min"/>
                    </div>
                    <div>
                      <Label>Base charge $</Label>
                      <Input type="number" step="0.01" value={estCfg.base_charge} onChange={(e) => setEstCfg({ ...estCfg, base_charge: e.target.value })}/>
                    </div>
                    <div>
                      <Label>Material $/unit</Label>
                      <Input type="number" step="0.01" value={estCfg.material_cost_per_unit} onChange={(e) => setEstCfg({ ...estCfg, material_cost_per_unit: e.target.value })}/>
                    </div>
                    <div>
                      <Label>Markup %</Label>
                      <Input type="number" step="0.1" value={estCfg.material_markup_pct} onChange={(e) => setEstCfg({ ...estCfg, material_markup_pct: e.target.value })}/>
                    </div>
                    <div>
                      <Label>Labor $/hr</Label>
                      <Input type="number" step="0.01" value={estCfg.labor_rate_per_hour} onChange={(e) => setEstCfg({ ...estCfg, labor_rate_per_hour: e.target.value })}/>
                    </div>
                    <div>
                      <Label>Production /hr</Label>
                      <Input type="number" step="0.01" value={estCfg.production_rate} onChange={(e) => setEstCfg({ ...estCfg, production_rate: e.target.value })} placeholder="units per hr"/>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="pt-3 mt-1 border-t border-zinc-100">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent mb-2">Re-engagement (optional)</div>
              <p className="text-xs text-zinc-500 mb-3">Override the business default when a completed job of this service should trigger a follow-up SMS. Leave blank to use the business default (from Settings).</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Interval (days)</Label>
                  <Input type="number" min={1} max={1095} placeholder="e.g. 335" value={form.reengagement_days ?? ""} onChange={(e)=>setForm({...form, reengagement_days: e.target.value})} data-testid="service-form-reeng-days"/>
                </div>
                <div className="col-span-2">
                  <Label>Message template</Label>
                  <Textarea rows={2} placeholder="Uses {customer_name}, {business_name}, {service}" value={form.reengagement_template || ""} onChange={(e)=>setForm({...form, reengagement_template: e.target.value})} data-testid="service-form-reeng-template"/>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="service-form-submit">{editId ? "Save" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
